import { defineMessages, useIntl } from 'react-intl';
import { useState } from 'react';
import { Money } from '@carbon/icons-react';
import { IconButtonAlignment } from '@carbon/react';

import CommonButton from 'Components/Common/CommonButton/CommonButton';
import { useAddOrRemovePointsMutation } from 'api/pointsEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import PointsHandler from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamInteractions/Interactions/PointsHandler/PointsHandler';
import useGetUserPermission from 'hooks/useGetUserPermission';
import useCurrentEntityUsesPoints from 'hooks/useCurrentEntityUsesPoints';

type Props = {
    teamMemberId: string;
    align?: IconButtonAlignment;
    onSuccess?: () => void;
}

const AddOrRemovePoints = ({
    teamMemberId,
    align = 'bottom',
    onSuccess,
}: Props) => {
    const intl = useIntl();

    const [open, setOpen] = useState(false);
    const currentEntityId = useGetCurrentEntityId();
    const [addRemovePoints, { isLoading }] = useAddOrRemovePointsMutation();
    const { isAdmin, isModerator, isOwner } = useGetUserPermission();
    const usesPoints = useCurrentEntityUsesPoints();

    if (!usesPoints) {
        return null;
    }

    const handleFormSubmit = async (reason: string, numberOfPointsToChange: number) => {
        if (!currentEntityId) {
            return;
        }

        await addRemovePoints({
            entityId: currentEntityId,
            data: {
                teamMemberId,
                description: reason,
                value: numberOfPointsToChange,
            },
        }).unwrap();
        onSuccess?.();
    };

    const onOpen = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    return (
        <>
            <CommonButton
                align={align}
                label={intl.formatMessage(messages.addOrRemoveLabel)}
                kind="ghost"
                disabled={!isAdmin && !isModerator && !isOwner}
                iconSize={21}
                icon={<Money />}
                onClick={onOpen}
            />
            <PointsHandler label={intl.formatMessage(messages.addOrRemoveLabel)} onClose={onClose} open={open} onSubmit={handleFormSubmit} isLoading={isLoading} />
        </>
    );
};

const messages = defineMessages({
    addOrRemoveLabel: {
        id: 'src.Screens.Dashboard.Team.Components.TeamCellHandler.TeamInteractions.Interactions.AddOrRemovePoints.AddOrRemovePoints.addOrRemoveLabel',
        defaultMessage: 'Dodaj lub usuń punkty',
    },
});

export default AddOrRemovePoints;
